import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ChevronLeft, Mail, Phone } from 'lucide-react';

const LOGO_URL = 'https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png';

export default function PolicyLayout({ title, subtitle, lastUpdated = 'April 2026', children }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020617] text-white relative overflow-hidden" data-testid="policy-page">
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-amber-600/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-amber-600/[0.04] rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/[0.06] backdrop-blur-md bg-[#020617]/80">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="policy-back-btn">
            <ChevronLeft className="w-4 h-4" />
            <img src={LOGO_URL} alt="DotCall" className="h-6 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/60 hover:text-white text-sm hidden sm:inline-flex">Sign In</Button>
            <Button onClick={() => navigate('/pricing')} className="bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-full px-5 font-semibold">Get Started</Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{title}</span>
          </h1>
          {subtitle && <p className="text-white/50 text-base">{subtitle}</p>}
          <p className="text-white/30 text-xs mt-2">Last updated: {lastUpdated}</p>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-[#0B1221]/70 backdrop-blur-sm p-6 md:p-10">
          <div className="prose prose-invert max-w-none policy-content">
            {children}
          </div>
        </div>

        {/* Contact footer */}
        <div className="mt-10 rounded-2xl border border-amber-500/20 bg-amber-600/[0.05] p-6 md:p-8">
          <h3 className="text-white font-semibold text-lg mb-4">Need Help or Have Questions?</h3>
          <p className="text-white/60 text-sm mb-5">For any queries related to this policy, please reach out to us:</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="tel:+918879851335" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition-colors">
              <div className="w-9 h-9 rounded-full bg-amber-600/20 flex items-center justify-center"><Phone className="w-4 h-4 text-amber-300" /></div>
              <div>
                <div className="text-xs text-white/40">Call us</div>
                <div className="text-sm text-white font-medium">+91 88798 51335</div>
              </div>
            </a>
            <a href="mailto:support@dotcall.io" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition-colors">
              <div className="w-9 h-9 rounded-full bg-amber-600/20 flex items-center justify-center"><Mail className="w-4 h-4 text-amber-300" /></div>
              <div>
                <div className="text-xs text-white/40">Email us</div>
                <div className="text-sm text-white font-medium">support@dotcall.io</div>
              </div>
            </a>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/[0.06] py-8 mt-8">
        <div className="max-w-5xl mx-auto px-6 text-center text-white/25 text-sm">
          Copyrights Under DotCall | All Rights Reserved
        </div>
      </footer>

      <style>{`
        .policy-content h2 { color: #fff; font-size: 1.35rem; font-weight: 700; margin-top: 1.75rem; margin-bottom: 0.75rem; font-family: 'Outfit', sans-serif; }
        .policy-content h3 { color: rgba(255,255,255,0.9); font-size: 1.05rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.5rem; }
        .policy-content p { color: rgba(255,255,255,0.6); font-size: 0.95rem; line-height: 1.7; margin-bottom: 0.85rem; }
        .policy-content ul { color: rgba(255,255,255,0.6); font-size: 0.95rem; line-height: 1.7; margin-bottom: 0.85rem; padding-left: 1.5rem; list-style: disc; }
        .policy-content ul li { margin-bottom: 0.35rem; }
        .policy-content strong { color: rgba(255,255,255,0.85); font-weight: 600; }
        .policy-content a { color: #fbbf24; text-decoration: underline; }
        .policy-content a:hover { color: #fcd34d; }
      `}</style>
    </div>
  );
}
